import React, {FC} from "react"
import {match as Match, RouteComponentProps} from "react-router-dom"

import DealByUrlSlug from "@irati/deals/apollo/query/DealByUrlSlug"
import RequireDealStatusOn from "@irati-web/perks/src/components/RequireDealStatusOn"
import {Deal, DealType} from "@irati/types"
import AppContainer from "@irati/ui/src/AppContainer"
import {HasPermissionTo} from "@irati/utils/permissions"
import mainTheme from "@irati/ui/src/global/theme"
import {IndexRedirect, NestedRoute, PartialRoute, withPartialRoute} from "@irati/utils/history/routing"
import DealInfo from "@irati-web/perks/src/containers/DealInfo"
import Deals from "@irati-web/perks/src/containers/Deals"
import DealSuccess from "@irati-web/perks/src/containers/DealSuccess"
import DealRedemptionError from "@irati-web/perks/src/containers/DealRedemptionError"
import DealPayment from "@irati-web/perks/src/containers/DealPayment"
import DealSelectProducts from "@irati-web/perks/src/containers/DealSelectProducts"
import TagLandingPage from "@irati-web/perks/src/containers/Tag"
import {LangLoader} from "@irati/utils/lang"
import {PERKS, SEARCH} from "@irati-web/app/src/routeConfig"

import MerchandiseInfo from "../containers/MerchandiseInfo"
import MerchandiseCheckout from "../containers/MerchandiseCheckout"
import MerchandiseError from "../containers/MerchandiseError"
import MerchandiseSuccess from "../containers/MerchandiseSuccess"
import {DealContextProvider} from "@irati/deals/hooks/useDeal"

interface InfoProps {
  match: Match
}

const Info = ({match}: InfoProps) => (
  <>
    <NestedRoute
      match={match.url}
      exact
      path="/:urlSlug"
      title="Reward"
      component={(props) => <MerchOrVoucher Merch={MerchandiseInfo} Voucher={DealInfo} {...props} />}
    />
    <NestedRoute
      match={match.url}
      exact
      path="/:urlSlug/redeem"
      component={(props) => <MerchOrVoucher Merch={MerchandiseError} Voucher={DealSelectProducts} {...props} />}
      title="Redeem"
    />
    <NestedRoute
      match={match.url}
      exact
      path="/:urlSlug/pay"
      title="Payment"
      component={(props) => <MerchOrVoucher Merch={MerchandiseCheckout} Voucher={DealPayment} {...props} />}
    />
    <NestedRoute
      match={match.url}
      path="/:urlSlug/error/:checkoutId/:paymentOrderId"
      title="Redemption error"
      component={DealRedemptionError}
    />
    <NestedRoute
      match={match.url}
      path="/:urlSlug/success/:redemptionId"
      title="Reward Redeemed"
      component={(props) => <MerchOrVoucher Merch={MerchandiseSuccess} Voucher={DealSuccess} {...props} />}
    />
  </>
)

interface Props {
  path: string
  location: Location
}

const FlexiRewardRoutes: FC<Props> = (props) => {
  const {path, location} = props

  const isPerksAccount = location.pathname.includes("/perks/account")

  return (
    <LangLoader buckets={[PERKS, SEARCH]}>
      <AppContainer mainBackground={isPerksAccount && mainTheme.colors.greys.white}>
        <HasPermissionTo permissions="wallet.flexiRewards">
          <NestedRoute match={path} path="/categories/:category?" title="Deals" component={Deals} useGrid={false} />
          <PartialRoute match={path} path="/info" component={Info} />
          <NestedRoute match={path} path="/tag/:tagIdOrUrl" title="Loading tag..." component={TagLandingPage} />
          <IndexRedirect match={{url: path}} to="/categories" />
        </HasPermissionTo>
      </AppContainer>
    </LangLoader>
  )
}

interface IMerchOrVoucher extends RouteComponentProps<{urlSlug: string}> {
  Merch: React.FC<{deal: Deal}>
  Voucher: React.FC<{deal: Deal}>
}
function MerchOrVoucher({Merch, Voucher, ...props}: IMerchOrVoucher) {
  const urlSlug = props.match?.params?.urlSlug
  if (!urlSlug) {
    return null
  }
  return (
    <DealByUrlSlug variables={{urlSlug, isRewards: true}} Error={RequireDealStatusOn}>
      {(res) => {
        const deal = res?.data?.dealBySlug
        if (!deal) return null
        if (res?.data?.dealBySlug?.type === DealType.Merchandise) {
          return (
            <DealContextProvider deal={deal}>
              <Merch {...props} deal={deal} />
            </DealContextProvider>
          )
        }
        // Voucher return deal components - used for both Perks and Flexi Rewards redemption flow
        return (
          <DealContextProvider deal={deal}>
            <Voucher {...props} deal={deal} />
          </DealContextProvider>
        )
      }}
    </DealByUrlSlug>
  )
}

export default withPartialRoute(FlexiRewardRoutes)
